import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  LocalShipping as ShippingIcon,
  DirectionsCar as CarIcon,
  People as PeopleIcon,
  Map as MapIcon,
  LocalGasStation as LocalGasStationIcon,
  Business as BusinessIcon,
  Menu as MenuIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Assessment as AssessmentIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, switchRole } = useAuth();
  const portalRole = user?.active_role || user?.role;
  const accessibleRoles = user?.accessible_roles?.length
    ? user.accessible_roles
    : portalRole
      ? [portalRole]
      : [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = useMemo(() => {
    let items = [];
    if (portalRole === 'customer') {
      items = [
        { text: 'Dashboard', path: '/customer/dashboard', icon: <DashboardIcon /> },
        { text: 'Quotes', path: '/customer/quotes', icon: <AssignmentIcon /> },
        { text: 'Orders', path: '/customer/orders', icon: <ShippingIcon /> },
        { text: 'Invoices', path: '/customer/invoices', icon: <ReceiptIcon /> },
        { text: 'Payment Methods', path: '/customer/payment-methods', icon: <CreditCardIcon /> },
        { text: 'Profile', path: '/customer/profile', icon: <PersonIcon /> },
      ];
    } else if (portalRole === 'driver') {
      items = [
        { text: 'Dashboard', path: '/driver/dashboard', icon: <DashboardIcon /> },
        { text: 'Jobs', path: '/driver/jobs', icon: <AssignmentIcon /> },
        { text: 'Map', path: '/driver/map', icon: <MapIcon /> },
        { text: 'Fuel depots', path: '/driver/depots', icon: <LocalGasStationIcon /> },
        { text: 'Schedule', path: '/driver/schedule', icon: <CalendarMonthIcon /> },
        { text: 'Reports & Trends', path: '/driver/reports', icon: <AssessmentIcon /> },
        { text: 'Profile', path: '/driver/profile', icon: <PersonIcon /> },
      ];
    } else if (portalRole === 'manager') {
      items = [
        { text: 'Dashboard', path: '/manager/dashboard', icon: <DashboardIcon /> },
        { text: 'Drivers', path: '/manager/drivers', icon: <PersonIcon /> },
        { text: 'Customers', path: '/manager/customers', icon: <PeopleIcon /> },
        { text: 'Jobs', path: '/manager/jobs', icon: <AssignmentIcon /> },
        { text: 'Invoices', path: '/manager/invoices', icon: <ReceiptIcon /> },
        { text: 'Reports & Trends', path: '/manager/reports', icon: <AssessmentIcon /> },
        { text: 'Pricing', path: '/manager/pricing', icon: <ReceiptIcon /> },
        { text: 'Vehicles', path: '/manager/vehicles', icon: <CarIcon /> },
        { text: 'Map', path: '/manager/map', icon: <MapIcon /> },
        { text: 'Fuel depots', path: '/manager/depots', icon: <LocalGasStationIcon /> },
        { text: 'Organization', path: '/manager/organization', icon: <BusinessIcon /> },
        { text: 'Profile', path: '/manager/profile', icon: <PersonIcon /> },
      ];
    }
    if (user?.is_superuser) {
      items = [
        ...items,
        {
          text: 'Users (admin)',
          path: '/admin/users',
          icon: <AdminPanelSettingsIcon />,
        },
      ];
    }
    return items;
  }, [portalRole, user?.is_superuser]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleLabels = { customer: 'Customer', driver: 'Driver', manager: 'Manager' };

  const handlePortalRoleChange = async (event) => {
    const next = event.target.value;
    const result = await switchRole(next);
    if (result.success) {
      navigate(`/${next}/dashboard`);
    }
  };

  const drawer = (
    <Box>
      <Toolbar />
      {accessibleRoles.length > 1 && (
        <Box sx={{ px: 2, pb: 1, display: { xs: 'block', md: 'none' } }}>
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel id="drawer-portal-role-label">View as</InputLabel>
            <Select
              labelId="drawer-portal-role-label"
              label="View as"
              value={portalRole}
              onChange={async (e) => {
                await handlePortalRoleChange(e);
                setMobileOpen(false);
              }}
            >
              {accessibleRoles.map((r) => (
                <MenuItem key={r} value={r}>
                  {roleLabels[r] || r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <Box sx={{ overflow: 'auto' }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  '&.Mui-selected': {
                    background: 'rgba(30, 58, 95, 0.08)',
                    borderLeft: '3px solid #1E3A5F',
                    '&:hover': {
                      background: 'rgba(30, 58, 95, 0.12)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#1E3A5F',
                    },
                    '& .MuiListItemText-primary': {
                      color: '#1E3A5F',
                      fontWeight: 600,
                    },
                  },
                  '&:hover': {
                    background: 'rgba(30, 58, 95, 0.05)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? '#1E3A5F' : '#666',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiTypography-root': {
                      color: location.pathname === item.path ? '#1E3A5F' : '#333',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + (isMobile ? 0 : 1),
          background: '#1E3A5F',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1, sm: 2 },
            gap: 0.5,
          }}
        >
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Logo size="small" variant="icon" />
            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              noWrap
              component="div"
              sx={{
                ml: 2,
                fontWeight: 600,
                color: '#ffffff',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              DND Diesel and Def Delivery
            </Typography>
            <Typography
              variant="subtitle2"
              noWrap
              component="div"
              sx={{
                ml: 2,
                fontWeight: 600,
                color: '#ffffff',
                display: { xs: 'block', sm: 'none' },
              }}
            >
              DND Delivery
            </Typography>
          </Box>
          {!isMobile && (
            <Typography
              variant="body2"
              sx={{
                mr: 2,
                color: 'rgba(255, 255, 255, 0.9)',
                display: { xs: 'none', md: 'block' },
              }}
            >
              {user?.email}
            </Typography>
          )}
          {accessibleRoles.length > 1 && !isMobile && (
            <FormControl
              size="small"
              sx={{
                minWidth: 140,
                mr: 2,
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.85)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                '& .MuiSvgIcon-root': { color: '#fff' },
                '& .MuiSelect-select': { color: '#fff', py: 0.75 },
              }}
            >
              <InputLabel id="portal-role-label">View as</InputLabel>
              <Select
                labelId="portal-role-label"
                label="View as"
                value={portalRole}
                onChange={handlePortalRoleChange}
              >
                {accessibleRoles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {roleLabels[r] || r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              color: '#ffffff',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiButton-startIcon': {
                marginRight: { xs: 0, sm: 1 },
              },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Logout
            </Box>
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="navigation"
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: { xs: 'min(280px, 85vw)', sm: drawerWidth },
                background: '#ffffff',
                borderRight: '1px solid rgba(30, 58, 95, 0.1)',
                pt: 'env(safe-area-inset-top, 0px)',
              },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                background: '#ffffff',
                borderRight: '1px solid rgba(30, 58, 95, 0.1)',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: { xs: 1, sm: 2, md: 3 },
          pt: { xs: 1.5, sm: 2, md: 3 },
          pb: {
            xs: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            sm: 2,
            md: 3,
          },
          minHeight: '100dvh',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100vw',
          overflowX: 'hidden',
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;



